import { useEffect, useState } from 'react';
import { useSearchParams, Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { CheckCircle, Package, ArrowRight } from 'lucide-react';
import axios from 'axios';
import { useCart } from '../context/CartContext';
import { Button } from '../components/ui/button';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const CheckoutSuccess = () => {
  const [searchParams] = useSearchParams();
  const sessionId = searchParams.get('session_id');
  const { clearCart } = useCart();
  const [status, setStatus] = useState('loading');
  const [paymentInfo, setPaymentInfo] = useState(null);

  useEffect(() => {
    const pollPaymentStatus = async (attempts = 0) => {
      const maxAttempts = 10;
      const pollInterval = 2000;

      if (attempts >= maxAttempts) {
        setStatus('timeout');
        return;
      }

      try {
        const res = await axios.get(`${API}/checkout/status/${sessionId}`);
        setPaymentInfo(res.data);

        if (res.data.payment_status === 'paid') {
          setStatus('success');
          clearCart();
          return;
        } else if (res.data.status === 'expired') {
          setStatus('expired');
          return;
        }

        // Continue polling
        setTimeout(() => pollPaymentStatus(attempts + 1), pollInterval);
      } catch (err) {
        console.error('Error checking payment status:', err);
        setStatus('error');
      }
    };

    if (sessionId) {
      pollPaymentStatus();
    } else {
      setStatus('error');
    }
  }, [sessionId, clearCart]);

  return (
    <div className="min-h-screen pt-28 pb-24 px-6 md:px-12 flex items-center justify-center">
      <div className="max-w-lg mx-auto text-center">
        {status === 'loading' && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
          >
            <div className="w-16 h-16 border-4 border-gold border-t-transparent rounded-full animate-spin mx-auto mb-6" />
            <h1 className="font-heading text-2xl mb-4">Processing Payment...</h1>
            <p className="text-muted-foreground">Please wait while we confirm your payment.</p>
          </motion.div>
        )}

        {status === 'success' && (
          <motion.div
            initial={{ opacity: 0, scale: 0.9 }}
            animate={{ opacity: 1, scale: 1 }}
          >
            <div className="w-20 h-20 rounded-full bg-green-500/10 flex items-center justify-center mx-auto mb-6">
              <CheckCircle className="h-10 w-10 text-green-500" />
            </div>
            <h1 className="font-heading text-3xl mb-4" data-testid="success-title">
              Thank You!
            </h1>
            <p className="text-muted-foreground mb-8">
              Your order has been confirmed and will be shipped soon.
            </p>
            
            {paymentInfo && (
              <div className="bg-card border border-white/10 p-6 mb-8 text-left">
                <div className="flex items-center gap-3 mb-4">
                  <Package className="h-5 w-5 text-gold" />
                  <span className="font-body">Order Details</span>
                </div>
                <div className="space-y-2 text-sm">
                  <div className="flex justify-between">
                    <span className="text-muted-foreground">Amount Paid</span>
                    <span className="text-gold font-accent">
                      ${(paymentInfo.amount_total / 100).toFixed(2)} {paymentInfo.currency?.toUpperCase()}
                    </span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-muted-foreground">Status</span>
                    <span className="text-green-500">Paid</span>
                  </div>
                </div>
              </div>
            )}

            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Button
                asChild
                className="bg-gold text-black hover:bg-gold-dark px-8 py-6 text-xs tracking-widest uppercase"
              >
                <Link to="/shop">
                  Continue Shopping
                  <ArrowRight className="ml-2 h-4 w-4" />
                </Link>
              </Button>
            </div>
          </motion.div>
        )}

        {(status === 'error' || status === 'timeout' || status === 'expired') && (
          <motion.div
            initial={{ opacity: 0, scale: 0.9 }}
            animate={{ opacity: 1, scale: 1 }}
          >
            <div className="w-20 h-20 rounded-full bg-red-500/10 flex items-center justify-center mx-auto mb-6">
              <Package className="h-10 w-10 text-red-500" />
            </div>
            <h1 className="font-heading text-3xl mb-4">
              {status === 'expired' ? 'Session Expired' : 'Something Went Wrong'}
            </h1>
            <p className="text-muted-foreground mb-8">
              {status === 'expired'
                ? 'Your payment session has expired. Please try again.'
                : 'We couldn\'t verify your payment. Please contact support if you were charged.'}
            </p>
            <Button
              asChild
              className="bg-gold text-black hover:bg-gold-dark px-8 py-6 text-xs tracking-widest uppercase"
            >
              <Link to="/cart">Return to Cart</Link>
            </Button>
          </motion.div>
        )}
      </div>
    </div>
  );
};

export default CheckoutSuccess;
